import { AdditionalInfoView } from '../types';
import { Virtualizer, VirtualItem } from '@tanstack/react-virtual';
import { IObservableArray, observe, IArrayDidChange } from 'mobx';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type LastRenderData = {
    lastRenderTopVirtualItemKey: number;
    lastRenderTopVirtualItemInnerOffset: number;
};

type UseScrollAnchorParams = {
    data: IObservableArray<AdditionalInfoView>;
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
    // Rows above the chronological data (pinned + divider + newer loader).
    // getLastRenderData subtracts this before indexing `data`. 0 when the flag
    // is off (no pinned section).
    chronologicalStartIndex: number;
    // Stick-to-newest (the renderer's synchronous anchor ref). When false — user
    // scrolled into history after a jump — a newer page prepended above must be
    // compensated so the reading position holds; when true we keep new arrivals
    // visible. Always true on the flag-off path.
    isStuckToNewest: boolean;
};

interface Changes {
    addedRows: AdditionalInfoView[];
    removedRows: AdditionalInfoView[];
}

export const useScrollAnchor = ({
    data,
    rowVirtualizer,
    chronologicalStartIndex,
    isStuckToNewest,
}: UseScrollAnchorParams): void => {
    const [changes, setChanges] = useState<Changes>({
        addedRows: [],
        removedRows: [],
    });

    useEffect(() => {
        // set up a mobx change observer to notify the hook whenever an additional info row is added or removed
        const disposer = observe(data, (change: IArrayDidChange<AdditionalInfoView>) => {
            if (change.type === 'splice') {
                setChanges({ addedRows: change.added, removedRows: change.removed });
            }
        });

        return () => disposer();
        // this should execute on mount only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lastRenderDataRef = useRef<LastRenderData>();
    const pendingScrollOffsetRef = useRef<number>();
    // Anchor to preserve when rows are added above the viewport. Resolved in the
    // post-commit layout effect against the FRESH measured position. See the
    // render-body comment below for why this can't be computed here.
    const pendingAddedAnchorRef = useRef<{ key: number; innerOffset: number }>();

    const { addedRows, removedRows } = changes;

    if (lastRenderDataRef.current) {
        const {
            lastRenderTopVirtualItemInnerOffset,
            lastRenderTopVirtualItemKey,
        } = lastRenderDataRef.current;

        if (addedRows.length > 0) {
            // Defer added-row scroll compensation to the post-commit layout
            // effect. The virtualizer's measurementsCache is stale in the render
            // body (the prior top item's `.start` does not yet include the
            // just-added rows) and flips stale→fresh across the multiple renders
            // a single insert triggers — so any offset computed here is
            // nondeterministic and lands one row off (RND-63506). Stash only the
            // cache-independent anchor (the prior top item + its inner offset);
            // the layout effect resolves it against the freshly measured `.start`.
            const lastRenderScrollOffset = rowVirtualizer.scrollOffset;
            // Compensate a prepend above the viewport. `scrollOffset !== 0`
            // preserves stick-to-newest at the top; `!isStuckToNewest` adds the
            // mid-history-at-scrollTop-0 case (after a jump). No conflict with
            // reanchorToNewest — both read the same anchor ref, so at scrollOffset
            // 0 exactly one acts.
            if (
                addedRows[0].id > lastRenderTopVirtualItemKey &&
                (lastRenderScrollOffset !== 0 || !isStuckToNewest)
            ) {
                pendingAddedAnchorRef.current = {
                    key: lastRenderTopVirtualItemKey,
                    innerOffset: lastRenderTopVirtualItemInnerOffset,
                };
            }
        }

        if (removedRows.length > 0) {
            compensateForRemovedRows({
                lastRenderTopVirtualItemInnerOffset,
                lastRenderTopVirtualItemKey,
                removedRows,
                rowVirtualizer,
            });
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        let renderNeeded = false;
        // Resolve a deferred added-row anchor against the now-fresh, post-commit
        // measurementsCache: the anchored item's `.start` now includes the
        // just-added rows, so `start + innerOffset` keeps it visually in place
        // with no row-height estimate (RND-63506).
        if (pendingAddedAnchorRef.current) {
            const { key, innerOffset } = pendingAddedAnchorRef.current;
            pendingAddedAnchorRef.current = undefined;
            const freshItem = rowVirtualizer.measurementsCache.find((item) => item.key === key);
            if (freshItem) {
                const target = freshItem.start + innerOffset;
                rowVirtualizer.scrollOffset = target;
                rowVirtualizer.scrollToOffset(target, { align: 'start' });
                renderNeeded = true;
            }
        }
        if (pendingScrollOffsetRef.current) {
            rowVirtualizer.scrollOffset = pendingScrollOffsetRef.current;
            rowVirtualizer.scrollToOffset(pendingScrollOffsetRef.current, {
                align: 'start',
            });
            pendingScrollOffsetRef.current = undefined;
            // We need to re-render if pendingScrollOffset was used to move the scrollOffset onto a new virtualItem
            // since there would be a blank space until re-render.
            renderNeeded = true;
        }

        // Note: this needs to be a layout effect to ensure that this function runs synchronously when this hook renders
        // which ensures that lastRenderDataRef is always up to date when the component renders
        lastRenderDataRef.current = getLastRenderData(
            rowVirtualizer,
            data,
            chronologicalStartIndex
        );

        if (addedRows.length > 0 || removedRows.length > 0) {
            clearChanges(renderNeeded);
        }
    });

    const clearChanges = (rerender: boolean): void => {
        if (rerender) {
            setChanges({ addedRows: [], removedRows: [] });
        } else {
            while (addedRows.length > 0) {
                addedRows.pop();
            }
            while (removedRows.length > 0) {
                removedRows.pop();
            }
        }
    };
};

interface CompenstateForRemovedRowsProps {
    lastRenderTopVirtualItemInnerOffset: number;
    lastRenderTopVirtualItemKey: number;
    removedRows: AdditionalInfoView[];
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

const compensateForRemovedRows = ({
    lastRenderTopVirtualItemInnerOffset,
    lastRenderTopVirtualItemKey,
    removedRows,
    rowVirtualizer,
}: CompenstateForRemovedRowsProps): void => {
    if (removedRows[0].id > lastRenderTopVirtualItemKey) {
        // items were removed above current scroll position
        const { measurementsCache } = rowVirtualizer;
        const lastRenderTopVirtualItem = measurementsCache.find(
            ({ key }) => key === lastRenderTopVirtualItemKey
        );

        if (lastRenderTopVirtualItem) {
            const sizeAboveTopVirtualItem = getSizeAboveVirtualItemWithRemovedRows(
                lastRenderTopVirtualItem,
                measurementsCache,
                removedRows
            );
            const nextScrollOffset = sizeAboveTopVirtualItem + lastRenderTopVirtualItemInnerOffset;
            rowVirtualizer.scrollOffset = nextScrollOffset;
            rowVirtualizer.scrollToOffset(nextScrollOffset, {
                align: 'start',
            });
        }
    }
};

const getSizeAboveVirtualItemWithRemovedRows = (
    virtualItem: VirtualItem,
    measurementsCache: VirtualItem[],
    removedRows: AdditionalInfoView[]
): number => {
    const itemsAboveVirtualItem = measurementsCache.slice(0, virtualItem.index);
    const removedItemIds = removedRows.map((item) => item.id);
    const itemsAboveVirtualItemAfterRemovedRow = itemsAboveVirtualItem.filter(
        (item) => !removedItemIds.includes(item.key as number)
    );
    return itemsAboveVirtualItemAfterRemovedRow.reduce((result, item) => result + item.size, 0);
};

export function getLastRenderData(
    virtualizer: Virtualizer<HTMLDivElement, Element>,
    data: IObservableArray<AdditionalInfoView>,
    chronologicalStartIndex: number
): LastRenderData | undefined {
    const virtualItems = virtualizer.getVirtualItems();

    if (data === undefined) {
        return;
    }
    // Anchor to the topmost *data* row: scan past leading non-data rows (pinned /
    // divider / newer loader) rather than reading virtualItems[0]. At the DESC
    // newer edge that first item is the newer loader (dataIndex < 0); blindly
    // taking it returned undefined → no compensation → runaway.
    for (const item of virtualItems) {
        const dataIndex = item.index - chronologicalStartIndex;
        if (dataIndex >= 0 && dataIndex < data.length && data[dataIndex] !== undefined) {
            return {
                lastRenderTopVirtualItemKey: data[dataIndex].id,
                lastRenderTopVirtualItemInnerOffset:
                    Math.ceil(virtualizer.scrollOffset) - item.start,
            };
        }
    }
    return;
}
